import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Video, Calendar, Clock, User, AlertCircle, Radio } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { isProfessional } from '../lib/subscription';
import { Meta } from '../components/Meta';
import { Auth } from '../components/Auth';
import { SubscriptionButton } from '../components/Subscription';
import { LiveChat } from '../components/LiveStream/LiveChat';
import { Room, RoomEvent } from 'livekit-client';

interface ScheduleItem {
  id: string;
  scheduled_date: string;
  title: string;
  host: string;
}

export function LiveStream() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStreamer, setIsStreamer] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [livekitUrl, setLivekitUrl] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<ScheduleItem[]>([]);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);

  const roomRef = useRef<Room | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Connect to LiveKit when stream goes live
  useEffect(() => {
    if (isLive) {
      connectToLiveKit();
    } else {
      disconnectFromLiveKit();
    }

    return () => {
      disconnectFromLiveKit();
    };
  }, [isLive]);

  const connectToLiveKit = async () => {
    try {
      setStreamError(null);

      // Get auth token from edge function
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        // Try anonymous connection
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/livekit-token`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ room: 'mindful-family-live', isHost: false }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to get LiveKit token');
        }

        const { token, url } = await response.json();
        await setupRoom(url, token);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/livekit-token`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ room: 'mindful-family-live', isHost: false }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get LiveKit token');
      }

      const { token, url } = await response.json();
      await setupRoom(url, token);
    } catch (err: any) {
      console.error('LiveKit connection error:', err);
      setStreamError(err.message || 'Failed to connect to stream');
    }
  };

  const setupRoom = async (url: string, token: string) => {
    const room = new Room();
    roomRef.current = room;

    // Handle remote tracks - attach both video and audio so sound plays
    room.on(RoomEvent.TrackSubscribed, (track, _publication, _participant) => {
      if (!videoRef.current) return;
      const el = videoRef.current;
      const existing = el.srcObject instanceof MediaStream ? el.srcObject : new MediaStream();
      existing.addTrack(track.mediaStreamTrack);
      el.srcObject = existing;
    });

    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      if (videoRef.current?.srcObject instanceof MediaStream) {
        const stream = videoRef.current.srcObject;
        stream.getTracks().forEach((t) => {
          if (t === track.mediaStreamTrack) stream.removeTrack(t);
        });
      }
      track.detach();
    });

    await room.connect(url, token);
    console.log('Connected to LiveKit as viewer');
  };

  const disconnectFromLiveKit = async () => {
    if (roomRef.current) {
      await roomRef.current.disconnect();
      roomRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError) throw authError;
        
        setIsAuthenticated(!!user);

        if (!user) {
          setLoading(false);
          return;
        }

        // Check if user has professional subscription (unified check)
        const pro = await isProfessional(user.id);
        setIsPro(pro);

        const { data: userData } = await supabase
          .from('users')
          .select('is_admin')
          .eq('id', user.id)
          .maybeSingle();
        setIsAdmin(!!userData?.is_admin);

        // Check if user is an authorized streamer
        const { data: streamerData } = await supabase
          .from('livekit_streamers')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .maybeSingle();
        setIsStreamer(!!streamerData);

        // Check if there's an active stream (from live_stream_settings or a dedicated table)
        const { data: streamSettings } = await supabase
          .from('live_stream_settings')
          .select('*')
          .eq('is_live', true)
          .maybeSingle();

        if (streamSettings) {
          setIsLive(true);
        }

        // Subscribe to live stream changes
        const channel = supabase
          .channel('live-stream-changes')
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'live_stream_settings'
          }, (payload) => {
            if (payload.new && typeof payload.new === 'object' && 'is_live' in payload.new) {
              setIsLive(payload.new.is_live === true);
            }
          })
          .subscribe();

        setLoading(false);

        // Fetch upcoming streams
        const { data: scheduleData } = await supabase
          .from('livestream_schedule')
          .select('*')
          .gte('scheduled_date', new Date().toISOString())
          .order('scheduled_date', { ascending: true });

        setSchedule(scheduleData || []);
      } catch (err) {
        console.error('Error checking access:', err);
        setError('Failed to load stream settings');
        setLoading(false);
      }
    };

    checkAccess();
  }, []);

  // Countdown timer for next scheduled stream
  const nextStream = schedule[0];
  useEffect(() => {
    if (!nextStream || isLive) {
      setCountdown(null);
      return;
    }
    const scheduledDate = new Date(nextStream.scheduled_date);
    const update = () => {
      const now = new Date();
      const diff = scheduledDate.getTime() - now.getTime();
      if (diff <= 0) {
        setCountdown(null);
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      });
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [nextStream, isLive]);

  // Subscribe to auth changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-text"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#F3F7EE] to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <div className="flex justify-center mb-8">
            <div className="p-4 bg-accent-base/10 rounded-full">
              <Video className="h-12 w-12 text-accent-text" />
            </div>
          </div>
          <h1 className="text-4xl font-gelica font-bold text-content mb-6">
            {t('liveStream.joinTitle')}
          </h1>
          <p className="text-xl text-content/70 mb-8 max-w-2xl mx-auto">
            {t('liveStream.joinSubtitle')}
          </p>
          <div className="space-y-4">
            <button
              onClick={() => setShowAuthModal(true)}
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-accent-text hover:bg-accent-text/90 transition-colors"
            >
              Sign in to Join Live Streams
            </button>
            <p className="text-sm text-content/60">
              Don't have an account? <button onClick={() => setShowAuthModal(true)} className="text-accent-text hover:text-accent-text/80">Create one now</button>
            </p>
          </div>
        </div>

        {showAuthModal && (
          <Auth onClose={() => setShowAuthModal(false)} />
        )}
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <div className="flex justify-center mb-8">
            <div className="p-4 bg-accent-base/10 rounded-full">
              <Video className="h-12 w-12 text-accent-text" />
            </div>
          </div>
          <h1 className="text-4xl font-gelica font-bold text-content mb-6">
            {t('liveStream.upgradeTitle')}
          </h1>
          <p className="text-xl text-content/70 mb-8 max-w-2xl mx-auto">
            {t('liveStream.upgradeSubtitle')}
          </p>
          <ul className="text-left max-w-md mx-auto mb-8 space-y-4">
            <li className="flex items-center text-content/80">
              <span className="w-2 h-2 bg-accent-text rounded-full mr-3"></span>
              Guided meditation and yoga sessions
            </li>
            <li className="flex items-center text-content/80">
              <span className="w-2 h-2 bg-accent-text rounded-full mr-3"></span>
              Nutrition and wellness Q&As
            </li>
            <li className="flex items-center text-content/80">
              <span className="w-2 h-2 bg-accent-text rounded-full mr-3"></span>
              Expert interviews and workshops
            </li>
            <li className="flex items-center text-content/80">
              <span className="w-2 h-2 bg-accent-text rounded-full mr-3"></span>
              Interactive community discussions
            </li>
          </ul>
          <SubscriptionButton className="w-full max-w-md mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Meta 
        title="Live Stream | Mindful Family"
        description="Join our live wellness sessions for real-time interaction with experts and community members."
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-accent-text/10 rounded-lg">
                <Video className="h-6 w-6 text-accent-text" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-content">
                  {t('liveStream.title')}
                </h1>
                <p className="text-sm text-gray-500">
                  {t('liveStream.subtitle')}
                </p>
              </div>
            </div>
            {isStreamer && (
              <button
                onClick={() => navigate('/live-stream/host')}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
              >
                <Radio className="h-4 w-4" />
                Go Live
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 py-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Live indicator */}
          {isLive && (
            <div className="bg-red-500 text-white px-4 py-2 rounded-lg flex items-center gap-2">
              <span className="w-3 h-3 bg-white rounded-full animate-pulse"></span>
              LIVE NOW - Stream is happening
            </div>
          )}

          <div className="bg-black rounded-lg overflow-hidden aspect-video">
            {isLive ? (
              streamError ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                  <div className="text-center text-white px-4">
                    <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
                    <p className="text-xl font-semibold">Stream unavailable</p>
                    <p className="text-gray-400 mt-2">{streamError}</p>
                  </div>
                </div>
              ) : (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted={false}
                  className="w-full h-full object-contain bg-gray-900"
                />
              )
            ) : (
              /* Stream offline - no iframe to avoid HLS manifest errors */
              <div className="w-full h-full overflow-y-auto flex flex-col items-center bg-gradient-to-b from-[#F3F7EE] via-[#F8FAF5] to-white px-6 py-8 pt-16 md:pt-20 pb-12">
                <div className="w-16 h-16 rounded-full bg-accent-text/15 flex items-center justify-center mb-4 flex-shrink-0">
                  <Video className="h-8 w-8 text-accent-text" />
                </div>
                <h2 className="text-xl md:text-2xl font-gelica font-bold text-[#2F4F3E] mb-1.5">
                  Stream is offline
                </h2>
                <p className="text-[#5A6B5A] mb-6 max-w-md text-center text-sm">
                  Check back soon or see when our next live session is scheduled below.
                </p>
                {nextStream && countdown ? (
                  <div className="bg-white/80 rounded-2xl border border-accent-text/20 p-4 shadow-sm flex-shrink-0">
                    <p className="text-sm font-medium text-[#2F4F3E] mb-3">Next session: {nextStream.title}</p>
                    <div className="flex justify-center gap-2">
                      {[
                        { value: countdown.days, label: 'days' },
                        { value: countdown.hours, label: 'hrs' },
                        { value: countdown.minutes, label: 'min' },
                        { value: countdown.seconds, label: 'sec' },
                      ].map((item, i) => (
                        <div key={i} className="text-center">
                          <div className="bg-white rounded-lg px-2.5 py-1.5 shadow-sm border border-gray-100">
                            <span className="text-base font-bold text-accent-text tabular-nums">
                              {item.value.toString().padStart(2, '0')}
                            </span>
                          </div>
                          <span className="text-[10px] text-[#5A6B5A] font-medium">{item.label}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-[#5A6B5A] mt-3 text-center">
                      {new Date(nextStream.scheduled_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}{' '}
                      · Hosted by {nextStream.host}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-[#5A6B5A]">No upcoming sessions scheduled. Check back soon!</p>
                )}
              </div>
            )}
          </div>
          
          {/* Schedule Section */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-content">{t('liveStream.upcomingSessions')}</h2>
            </div>
            
            {/* Sessions List */}
            <div className="divide-y divide-gray-100">
              {schedule.map((item) => {
                const scheduledDate = new Date(item.scheduled_date);
                const isToday = scheduledDate.toDateString() === new Date().toDateString();
                const isTomorrow = scheduledDate.toDateString() === new Date(Date.now() + 86400000).toDateString();
                
                return (
                  <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-content">{item.title}</h3>
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-content/60">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1.5" />
                            {scheduledDate.toLocaleDateString('en-US', { 
                              weekday: 'short',
                              month: 'short', 
                              day: 'numeric',
                              year: scheduledDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                            })}
                          </div>
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1.5" />
                            {scheduledDate.toLocaleTimeString('en-US', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </div>
                          <div className="flex items-center">
                            <User className="h-4 w-4 mr-1.5" />
                            {item.host}
                          </div>
                        </div>
                      </div>
                      <div className="ml-4">
                        {isToday ? (
                          <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-accent-text text-white">
                            Today
                          </span>
                        ) : isTomorrow ? (
                          <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                            Tomorrow
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Upcoming
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {schedule.length === 0 && (
                <div className="p-8 text-center text-content/60">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No upcoming sessions scheduled</p>
                  <p className="text-sm mt-1">Check back soon for new live stream sessions</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Live Chat */}
        <div className="lg:col-span-1">
          <LiveChat 
            onSignInClick={() => setShowAuthModal(true)}
            isAdmin={isAdmin}
          />
        </div>
      </div>
      </div>
    </div>
  );
}